// Constants
import { STATUS_CODES } from "@/app/constants/status-constants";
import { NOTIFICATION_TYPE, NOTIFICATION_PERIOD } from "@/app/constants/notification-constants";

// Services
import breedService from "@/app/services/main/breed/breed-service.js";
import notificationService from "@/app/services/main/notification/notification-service.js";
import PetsService from "@/app/services/main/pets/pets-service";

// Mixins
import { toastMessageMixins } from "@/app/mixins/utils/toast-message-mixins";
import { userMixins } from "@/app/mixins/modules/user-mixins.js";
import { switchPetMixins } from "@/app/mixins/modules/switch-pet-mixins";
import { tableMixins } from "@/app/mixins/utils/table-mixins.js";
import { imageCompressorMixins } from "@/app/mixins/utils/image-compressor-mixins";
import { dateMixins } from "@/app/mixins/utils/date-mixins";

// Componenets
const reminderTable = () =>
    import(
        /* webpackPrefetch: true, webpackChunkName: "reminder-table-component" */ "@/app/views/main-view/general-info/modules/reminder-table-component/reminder-table-component.vue"
    );
const documentTable = () =>
    import(
        /* webpackPrefetch: true, webpackChunkName: "document-table-component" */ "@/app/views/main-view/general-info/modules/document-table-component/document-table-component.vue"
    );
const FileUpload = () =>
    import(
        /* webpackPrefetch: true, webpackChunkName: "file-upload" */ "@/app/views/main-view/_shared/_components/file-upload/file-upload.vue"
    );
const DashboardContainer = () =>
    import(
        /* webpackPrefetch: true, webpackChunkName: "dashboard-container" */ "@/app/views/main-view/dashboard/containers/dashboard-container/dashboard-container.vue"
    );

export default {
    name: "GeneralInfoContainer",
    components: {
        reminderTable,
        documentTable,
        FileUpload,
        DashboardContainer,
    },
    mixins: [toastMessageMixins, userMixins, switchPetMixins, tableMixins, imageCompressorMixins, dateMixins],
    data() {
        return {
            styleIcon: "width: 38px; margin-right:10px; margin-left:-10px; margin-top:-10px;",
            images: {
                GENERAL: require("@/assets/images/general-info/dog-generalinfo.png"),
                HEALTH: require("@/assets/images/general-info/dog-health.png"),
                EXERCISE: require("@/assets/images/general-info/dog-exercise.png"),
                AGE: require("@/assets/images/_shared/success-dog.png"),
            },
            theads: [
                { name: "Age", style: "width: 70%;" },
                { name: "Date", style: "width: 30%;" },
            ],
            tabs: [
                {
                    label: "Diary",
                    id: 1,
                    active: true,
                },
                {
                    label: "Documents",
                    id: 2,
                    active: false,
                },
                {
                    label: "Breed Information",
                    id: 3,
                    active: false,
                },
            ],
            breedInfo: {},
            isAPILoading: false,
            imageToCrop: "",
            imageSrc: "",
            fileBlob: null,
            hasImage: false,
        };
    },
    computed: {
        infoId() {
            return this.$store.getters.getGenInfoId;
        },
        getActiveTab() {
            return this.tabs.find((tab) => tab.active);
        },
        reminder() {
            return this.$store.getters.getReminder;
        },
    },
    watch: {
        infoId(newVal) {
            // check if infoId is present to trigger alert
            if (newVal) {
                this.isAPILoading = true;
                this.setActiveTab(3);
                this.showNotificationPopups(newVal);
            }
        },
        reminder(newVal) {
            if (newVal) {
                this.showReminder(newVal);
            }
        },
    },
    created() {
        this.getDataGeneralInfo();
        this.getPetData();
        // check if infoId is present to trigger alert
        if (this.infoId) {
            this.showNotificationPopups(this.infoId, "NOTIF");
        }

        if (this.reminder) {
            this.showReminder(this.reminder);
        }
    },
    methods: {
        refreshActivePetData() {
            this.getDataGeneralInfo();
            this.getPetData();
        },
        getPetData(secondaryImageUpdated = false) {
            this.isAPILoading = true;
            PetsService.getPetById(this.getActivePet.id).then((res) => {
                this.isAPILoading = false;
                if (res.status === STATUS_CODES.SUCCESS) {
                    const updatedPet = { ...this.getActivePet, ...res.data };
                    if (secondaryImageUpdated) {
                        let updatedUserPet = this.userDetails.pets.map((item) => {
                            if (item.id === updatedPet.id) {
                                Object.assign(item, updatedPet);
                            }
                            return item;
                        });

                        this.$store.commit("setActivePet", updatedPet);
                        this.$store.commit("setUserDetails", { ...this.userDetails, pets: [...updatedUserPet] });
                    }

                    if (this.getActivePet.imageUrl != res.data.imageUrl) {
                        this.$store.commit("setActivePet", updatedPet);
                    }
                    this.populateData();
                } else {
                    const { ERROR_GET_PET_DETAILS } = this.toastMessages.pet();
                    this.showAPIErrorToast(ERROR_GET_PET_DETAILS, res);
                }
            });
        },
        populateData() {
            const { imageUrl, secondaryImage } = this.getActivePet;

            this.hasImage = false;

            let firstImage = imageUrl.length ? imageUrl : null;

            this.imageSrc = secondaryImage ? secondaryImage : firstImage;

            if (this.imageSrc) this.hasImage = true;
        },
        showNotificationPopups(id, type) {
            // show notification modal, infoId coming from selected notification
            breedService.getBreedInfoByID(id).then((res) => {
                if (res.status === STATUS_CODES.SUCCESS) {
                    this.isAPILoading = false;
                    const breedInfo = res.data.data;
                    // set notification details
                    // set title based to notification period
                    const period = NOTIFICATION_PERIOD[breedInfo.infoTypeCode]
                        ? NOTIFICATION_PERIOD[breedInfo.infoTypeCode]
                        : "x months";

                    const title =
                        type === "NOTIF"
                            ? `${this.getActivePet.name} has turned ${period} today!`
                            : `${this.getActivePet.name} has turned ${period}!`;
                    this.$swal({
                        imageHeight: "30px",
                        imageUrl: `${require("@/assets/images/_shared/success-dog.png")}`,
                        imageAlt: "Health Dog",
                        title,
                        html: breedInfo.description.replace(/\\n/g, "").replace(/\n/g, "<br/>"),
                        confirmButtonColor: "#4B8BFF",
                        confirmButtonText: "Got it",
                        customClass: {
                            container: "container-notification",
                            header: "header-notification",
                            content: "content-notification",
                            title: "title-notification",
                            confirmButton: "confirm-button-notification",
                        },
                    }).then(() => {
                        // clear ID to prevent the modal to show again
                        this.$store.commit("clearGenInfoId");
                    });
                } else {
                    const { ERROR_GET_BREED_INFO } = this.toastMessages.breed();
                    this.showAPIErrorToast(ERROR_GET_BREED_INFO, res);
                }
            });
        },
        getDataGeneralInfo() {
            this.isAPILoading = true;

            // for now pass the first breed in pet list
            breedService.getBreedInfo(this.getActivePet.breed.id).then((res) => {
                if (res.status === STATUS_CODES.SUCCESS) {
                    this.isAPILoading = false;
                    // map the results to format it in the tabs
                    this.breedInfo = res.data.data.map(({ infoTypeCode, id, description }) => {
                        return {
                            label: NOTIFICATION_PERIOD[infoTypeCode],
                            id,
                            img: this.images[infoTypeCode],
                            description,
                        };
                    });

                    const queryParams = {
                        page: 1,
                        limit: 10,
                        sortBy: "",
                        desc: true,
                        f_notificationTypeCode: NOTIFICATION_TYPE.GEN_INFO,
                    };

                    notificationService.getNotification(this.getActivePet.id, queryParams).then((resp) => {
                        if (resp.status === STATUS_CODES.SUCCESS) {
                            this.tableContent = {
                                tableData: resp.data.data,
                            };
                        }
                    });
                } else {
                    const { ERROR_GET_GENERAL_INFO } = this.toastMessages.pet();
                    this.showAPIErrorToast(ERROR_GET_GENERAL_INFO, res);
                }
            });
        },
        setActiveTab(selectedTab) {
            this.tabs = this.tabs.map((tab) => {
                // set the selected tab to active
                tab.active = tab.id === selectedTab;

                return tab;
            });
        },
        async onUploadImage(event) {
            let valid = await this.validateImage(event.target.files[0]);

            // compress image if more than 2mb
            const file = await this.compressImage(event.target.files[0]);

            // do nothing when user cancels Upload
            if (!valid) {
                // reset uploader
                this.$refs.uploader.value = "";
                return;
            }

            this.isImageChanged = true;
            this.fileBlob = [file];

            // show preview of pet image
            let reader = new FileReader();
            reader.onload = () => {
                this.imageToCrop = reader.result;
                this.$refs.modalFileUpload.show();
            };
            //updateSecondImage
            reader.readAsDataURL(file);
        },
        validateImage(img) {
            // if File doesn't exist means user cancelled file upload.
            if (!img) {
                return false;
            }

            // get size in binary format
            let size = Math.round(img.size / 1024);
            let errorMessage = "";
            let isValid = true;

            // check File Size if exceeds 5 MB
            if (size > 20000 && isValid) {
                errorMessage = "File Size must not exceed 20 MB.";
                isValid = false;
            }

            // check if File Type is an Image
            if ((img.type.includes("image/tiff") || !img.type.includes("image")) && isValid) {
                errorMessage = "Unsupported File Format.";
                isValid = false;
            }

            if (!isValid) {
                this.showCustomErrorToast("Unable to upload file.", errorMessage);
            }

            return isValid;
        },
        setImageCrop({ image, blob }) {
            this.isImageChanged = true;
            this.imageSrc = image;
            this.fileBlob = blob;
            let secondaryImage = this.fileBlob ? this.fileBlob[0] : "";
            PetsService.updateSecondImage(this.getActivePet.id, secondaryImage).then(async (res) => {
                if (res.status === 204) {
                    this.showCustomSuccessToast(null, "Successfully Updated Photo!");
                    this.getPetData(true);
                } else {
                    this.showCustomErrorToast(null, "Something went wrong. Please contact your administrator.");
                    this.populateData();
                }
            });
        },
        clearFileInput(event) {
            event.target.value = "";
        },
        getIcon() {
			return require(`@/assets/images/_shared/shop.png`);
		},
        gotoShop(){
            this.$router.push('/products');
        },
        showReminder(reminder) {
            const reminderDetails = {
                id: reminder.targetId,
                type: reminder.title,
                note: reminder.note,
                schedule: reminder.schedule,
                status: reminder.reminderStatus,
                frequency: reminder.frequency,
            };
            this.setActiveTab(1);

            setTimeout(() => {
                this.$refs.reminderTable.showAddEditReminderModal("Edit", reminderDetails);
            }, 1000);

            this.$store.commit("clearReminder");
        },

    },
};
